import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Sparkles } from "lucide-react";

export default function Auth() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(email, password);
        toast.success("Welcome!");
        navigate(u?.role === "admin" ? "/admin" : "/dashboard");
        return;
      } else {
        await register(name, email, password);
      }
      toast.success("Welcome!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* form side */}
      <div className="flex items-center justify-center px-6 py-16 relative">
        <div className="pointer-events-none absolute -top-20 -left-20 w-[28rem] h-[28rem] rounded-full bg-gold/15 blur-[120px]" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
          <div className="flex items-center gap-2 font-display font-black text-xl mb-10 cursor-pointer" onClick={() => navigate("/")}>
            <Sparkles className="w-5 h-5 text-gold" /> Unveil
          </div>
          <h1 className="font-display font-black text-4xl tracking-tight mb-2">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="text-foreground/60 mb-8">{mode === "login" ? "Log in to plan your reveals." : "Start hosting unforgettable reveals."}</p>

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-foreground/60">Name</Label>
                <Input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="h-14 mt-2 bg-input border-white/10 focus:border-white/40 rounded-xl" placeholder="Alex" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-foreground/60">Email</Label>
              <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-14 mt-2 bg-input border-white/10 focus:border-white/40 rounded-xl" placeholder="you@email.com" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-foreground/60">Password</Label>
              <Input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-14 mt-2 bg-input border-white/10 focus:border-white/40 rounded-xl" placeholder="••••••••" />
            </div>
            <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full h-14 rounded-xl bg-gold hover:bg-gold/90 font-semibold text-base">
              {loading ? "Please wait…" : mode === "login" ? "Log in" : "Sign up"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-foreground/60">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button data-testid="auth-toggle-btn" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-gold font-semibold hover:underline">
              {mode === "login" ? "Create an account" : "Log in"}
            </button>
          </p>
          <p className="mt-8 text-xs text-foreground/30">Demo host: host@celebrate.com / reveal123</p>
        </motion.div>
      </div>

      {/* image side */}
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1681641095463-b4d3693a0ee3" alt="Celebration reaction" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-display font-black text-4xl text-white leading-tight">The look on their faces is the whole point.</p>
        </div>
      </div>
    </div>
  );
}
